<template>
    <BaseField
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        :custom-theme-base="customThemeBase"
        v-bind="$attrs"
    >
        <div
            v-if="restrictMinutes && type === 'datetime-local'"
            class="flex flex-col gap-2 sm:flex-row sm:gap-2"
        >
            <div>
                <input
                    v-bind="$attrs"
                    ref="dateInput"
                    type="date"
                    :name="`${name}_date`"
                    :class="inputClassObject"
                    :placeholder="placeholder"
                    :disabled="isDisabled"
                    :value="dateValue"
                    :min="min"
                    :max="max"
                    data-spec-class="field-datetime__date"
                    @input="handleDateInput"
                    @blur="hasAutoDirty ? vuelidateField?.$touch() : () => {}"
                />
            </div>

            <div>
                <BaseFieldTime
                    v-model="timeValue"
                    :name="`${name}_time`"
                    :is-disabled="isDisabled"
                    :vuelidate-field="vuelidateField"
                    :has-auto-dirty="hasAutoDirty"
                    data-spec-class="field-datetime__time"
                    :custom-theme-base="FormTheme.CustomerArea"
                    :restrict-minutes="restrictMinutes"
                    :step-value="stepValue"
                    @update:model-value="handleTimeChange"
                />
            </div>
        </div>

        <div
            v-else
            :class="['relative rounded-md', wrapperClassAddition]"
        >
            <input
                v-bind="$attrs"
                ref="input"
                :type="type"
                :name="name"
                :class="inputClassObject"
                :placeholder="placeholder"
                :disabled="isDisabled"
                :value="modelValue"
                :step="step"
                :min="min"
                :max="max"
                data-spec-class="field-datetime__input"
                @input="handleInput"
                @blur="hasAutoDirty ? vuelidateField?.$touch() : () => {}"
            />

            <div
                v-if="errorMessagesTotal.length"
                class="absolute inset-y-0 right-0 flex items-center pr-3"
                data-spec-class="field-datetime__error-icon"
            >
                <ExclamationCircleIcon
                    class="h-5 w-5 text-danger"
                    aria-hidden="true"
                />
            </div>
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineExpose, defineProps, ref, withDefaults } from 'vue';
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid';
import { FormTheme, Theme } from '@/types';
import BaseField from './BaseField.vue';
import BaseFieldTime from './BaseFieldTime.vue';
import { useField } from '@/composables/field';

const emit = defineEmits(['update:modelValue']);

const { normalThemeFieldClasses, errorThemeFieldClasses, disabledThemeFieldClasses } = useField();

const input = ref<HTMLInputElement | null>(null);
const dateInput = ref<HTMLInputElement | null>(null);

function focus() {
    if (input.value) {
        input.value.focus();
    } else if (dateInput.value) {
        dateInput.value.focus();
    }
}

function select() {
    if (input.value) {
        input.value.select();
    } else if (dateInput.value) {
        dateInput.value.select();
    }
}

defineExpose({ focus, select });

interface Props {
    name: string;
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    type?: 'date' | 'datetime-local' | 'time';
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    wrapperClassAddition?: string;
    customThemeBase?: FormTheme;
    hasAutoDirty?: boolean;
    step?: string | number;
    restrictMinutes?: boolean;
    stepValue?: number;
    min?: string;
    max?: string;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    type: 'datetime-local',
    modelValue: undefined,
    vuelidateField: undefined,
    errorMessages: undefined,
    wrapperClassAddition: undefined,
    customThemeBase: undefined,
    hasAutoDirty: true,
    step: 1800,
    restrictMinutes: false,
    stepValue: 30,
    min: undefined,
    max: undefined,
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => {
    const external = props.errorMessages ?? [];
    return [...vuelidateErrors.value, ...external];
});

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }
    return Theme.Normal;
});

const inputClassObject = computed(() => ({
    [normalThemeFieldClasses]: theme.value === Theme.Normal,
    [errorThemeFieldClasses]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));

const dateValue = computed(() => {
    if (!props.modelValue) return '';
    return props.modelValue.split('T')[0];
});

const timeValue = computed({
    get() {
        if (!props.modelValue) return '09:00'; // Default to 9:00 AM
        const timePart = props.modelValue.split('T')[1];
        return timePart ? timePart.substring(0, 5) : '09:00'; // Get HH:MM format
    },
    set(value: string) {
        const currentDate = dateValue.value || new Date().toISOString().split('T')[0];
        const newDateTime = `${currentDate}T${value}`;
        emit('update:modelValue', newDateTime);
    },
});

function handleDateInput(event: Event) {
    const newDate = (event.target as HTMLInputElement).value;
    const currentTime = timeValue.value || '09:00';
    const newDateTime = `${newDate}T${currentTime}`;
    emit('update:modelValue', newDateTime);
}

function handleTimeChange(newTime: string) {
    const currentDate = dateValue.value || new Date().toISOString().split('T')[0];
    const currentTime = timeValue.value || '09:00';
    const currentHour = parseInt(currentTime.split(':')[0]);
    const newHour = parseInt(newTime.split(':')[0]);

    let adjustedDate = currentDate;

    if (currentHour === 23 && newHour === 0) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() + 1);
        adjustedDate = date.toISOString().split('T')[0];
    } else if (currentHour === 0 && newHour === 23) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - 1);
        adjustedDate = date.toISOString().split('T')[0];
    }

    const newDateTime = `${adjustedDate}T${newTime}`;
    emit('update:modelValue', newDateTime);
}

function handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    emit('update:modelValue', value);
}
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
